import { util } from 'echarts/core';
import { type XAXisOption } from 'echarts/types/dist/shared';
import AxisPointerModel from 'echarts/lib/component/axisPointer/AxisPointerModel';
import type { Option } from '../../../core/option';
import { type ThemeParser } from '../../../core/theme';
import { overrideIfUndefined } from '../../../util/data';
import { measureTextWidth, normalizeToArray } from '../../../util/utils';
import { judgeIsDynamicBar } from '../series/dynamicBar';
import { FONT_LINEHEIGHT_LIST, FONT_SIZE_LIST, getFontColor } from '../static/font';
import { getCoordBoundingRect } from '../../../model/util/coord';
import { getAxisTickStyle } from '../static/color';

type DvXAxisOption = Option['xAxis'];

function islabelWidthBeyondXWidth(
  data: string[],
  fontStyle: {
    fontSize: number;
    fontFamily?: string;
    fontWeight?: string | number;
  },
  xAxisWidth: number
) {
  const DEFAULT_MARGIN = 16;
  const labelWidth = data.reduce((prev, cur, i) => {
    return (
      prev +
      measureTextWidth(cur, fontStyle.fontSize, fontStyle.fontFamily, fontStyle.fontWeight) +
      (i === data.length - 1 ? 0 : DEFAULT_MARGIN)
    );
  }, 0);
  return labelWidth > xAxisWidth;
}

/**
 * 预处理xAxis组件的配置项。
 * 统一处理成配置项数组。
 * @param option
 */
function preprocessPCXAxisOption(option: Parameters<ThemeParser>[0]) {
  option.xAxis = normalizeToArray(option.xAxis || {}) as DvXAxisOption[];
}

/**
 *  问财条形图的xAxis默认配置。
 * @param option
 */
function parseDynamicHistogramXAxis(option: Parameters<ThemeParser>[0]) {
  option.xAxis = option.xAxis.map((xAxis: DvXAxisOption) => {
    const defaultOption: DvXAxisOption = {
      position: 'top',
      axisLabel: {
        color: '#999999',
        fontSize: 12,
        fontWeight: 400,
        formatter(n: number) {
          return Math.round(n);
        }
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          dashOffset: 2
        }
      },
      animationDuration: 300,
      animationDurationUpdate: 300
    };
    return util.merge(xAxis || {}, defaultOption);
  }) as DvXAxisOption[];
}

/**
 * pc端范式x轴布局
 * 范式要求x轴刻度之间至少保持16px的间隔
 * 若间隔小于16px，则隐藏第偶数个刻度
 * 若隐藏刻度后间隔仍然小于16px，则继续执行隐藏逻辑知道间隔大于等于16px
 * @param args
 */
export function parseXAxisInterval(...args: Parameters<ThemeParser>) {
  const [option, chart] = args;
  const chartDom = chart.getDom() as HTMLDivElement;
  option.xAxis = normalizeToArray(option.xAxis);
  option.xAxis.forEach(el => {
    el.axisLabel = overrideIfUndefined(el.axisLabel, {});
    const gridOption = normalizeToArray(option.grid).find((_, i) => {
      const gridIndex = el.gridIndex || 0;
      return gridIndex === i;
    });
    const coord = getCoordBoundingRect(chartDom, gridOption);
    const dvPadding = el.dvPadding || [0, 0];
    const xAxisWidth = coord.width - dvPadding[0] - dvPadding[1];
    const fontStyle = {
      fontSize: el.axisLabel.fontSize || FONT_SIZE_LIST.axisLabel,
      fontWeight: el.axisLabel.fontWeight,
      fontFamily: el.axisLabel.fontFamily
    };
    const formatter = el.axisLabel?.formatter || (value => value);
    const { data } = el;
    const originFormattedData = data.map(formatter);
    let tempData = [...originFormattedData];
    el.axisLabel.interval = (index: number) => {
      const dataZoomModel = chart.getECharts().getModel().getComponent('dataZoom');
      if (dataZoomModel) {
        const { startValue, endValue } = dataZoomModel.option;
        tempData = originFormattedData.slice(startValue, endValue + 1);
      }
      let formattedData = [...tempData];
      while (islabelWidthBeyondXWidth(formattedData, fontStyle, xAxisWidth)) {
        formattedData = formattedData.filter((_, j) => j % 2 === 0);
        if (formattedData.length === 1) {
          break;
        }
      }
      const displayLabelsIndex = [];
      formattedData.forEach((f: string) => {
        const target: number = originFormattedData.findIndex(o => o === f);
        if (target > -1) {
          displayLabelsIndex.push(target);
        }
      });
      return displayLabelsIndex.includes(index);
    };
  });
}

/**
 * @todo 类型判断后面找一下
 * @param option
 */
export function parsePCXAxisOption(
  option: Parameters<ThemeParser>[0],
  chart: Parameters<ThemeParser>[1]
) {
  const themeMode = chart.getModel().getThemeName().includes('light') ? 'light' : 'dark';
  AxisPointerModel.defaultOption.label.borderRadius = 0;
  (option.xAxis as DvXAxisOption[]).forEach((xAxisOption: DvXAxisOption) => {
    // PC端范式规范默认配置
    // 颜色和网格线保持一致  TODO
    // 网格线颜色分为黑白灰三个主题： 白：#000 0.12；黑：#fff 0.2；  灰（#1c1d21）：#fff 0.12.
    const defaultAxis: XAXisOption = {
      dvPadding: [0, 0],
      axisLine: {
        show: false
      },
      axisTick: {
        show: true,
        length: 4,
        lineStyle: getAxisTickStyle(themeMode)
      },
      axisLabel: {
        show: true,
        dvAlignEdge: true,
        margin: 8,
        fontSize: FONT_SIZE_LIST.axisLabel,
        color: getFontColor('axisLabel', themeMode)
      },
      axisPointer: {
        show: true,
        type: 'line',
        label: {
          backgroundColor: '#3366FF',
          padding: [3, 4, 1, 4]
        }
      }
    };
    if (xAxisOption.name) {
      // PC端范式规范默认配置
      // 距离x轴线的距离
      defaultAxis.nameGap = 29;
      defaultAxis.nameLocation = 'middle';
      defaultAxis.nameTextStyle = {
        fontSize: FONT_SIZE_LIST.axisName,
        color: getFontColor('axisName', themeMode),
        lineHeight: FONT_LINEHEIGHT_LIST.axisName
      };
    }

    util.merge(xAxisOption, defaultAxis);
  });
}

export function parseXAxisLine(option: Parameters<ThemeParser>[0]) {
  const xAxisArr = normalizeToArray(option.xAxis);
  // data中数据供x轴使用
  xAxisArr.forEach(xAxis => {
    xAxis.boundaryGap = overrideIfUndefined(xAxis.boundaryGap, false);
    xAxis.axisLine = util.merge({ show: false }, xAxis.axisLine || {}, true);
  });
}

export function xAxisThemeParse(...args: Parameters<ThemeParser>): ReturnType<ThemeParser> {
  const [option] = args;
  const series = normalizeToArray(option.series);
  // option.xAxis的预处理，默认将xAxis处理成数组.
  preprocessPCXAxisOption(option);
  if (judgeIsDynamicBar(series)) {
    parseDynamicHistogramXAxis(option);
  }
}
